import type React from 'react';
import type { CalculationResult, AmortizationSummary, YearlyScheduleGroup } from './types';
import { formatCurrency, formatShortCurrency } from './calculations';

export interface AmortizationScheduleProps {
  calculation: AmortizationSummary;
  yearlySchedule: YearlyScheduleGroup[];
  calcResult: CalculationResult;
  viewMode: 'yearly' | 'monthly';
  setViewMode: (mode: 'yearly' | 'monthly') => void;
  expandedYears: Record<number, boolean>;
  toggleYearExpand: (yearNumber: number) => void;
}

export const AmortizationSchedule: React.FC<AmortizationScheduleProps> = ({
  calculation,
  yearlySchedule,
  calcResult,
  viewMode,
  setViewMode,
  expandedYears,
  toggleYearExpand,
}) => {
  return (
    <div className="mt-10 bg-white rounded-3xl p-6 border border-gray-200 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-4">
        <div>
          <h2 className="text-lg font-extrabold text-gray-900">Repayment Amortization Schedule</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Breakdown of opening balance, principal repayment, interest component, and closing
            balance.
          </p>
        </div>

        {/* Toggle View Mode */}
        <div className="inline-flex bg-gray-100 p-1 rounded-xl shrink-0 self-start sm:self-auto">
          <button
            onClick={() => setViewMode('yearly')}
            className={`px-3 py-1.5 rounded-lg text-xs font-extrabold transition-all cursor-pointer ${
              viewMode === 'yearly'
                ? 'bg-white text-brand-800 shadow-xs'
                : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            Yearly Summary
          </button>
          <button
            onClick={() => setViewMode('monthly')}
            className={`px-3 py-1.5 rounded-lg text-xs font-extrabold transition-all cursor-pointer ${
              viewMode === 'monthly'
                ? 'bg-white text-brand-800 shadow-xs'
                : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            All Months
          </button>
        </div>
      </div>

      {/* Schedule View Content */}
      <div className="mt-6 overflow-x-auto">
        {calcResult.error ? (
          <div className="py-8 text-center text-sm text-amber-700 bg-amber-50/50 rounded-2xl border border-amber-200 font-medium">
            Amortization schedule is unavailable while loan parameters are invalid.
          </div>
        ) : viewMode === 'yearly' ? (
          <div className="space-y-3">
            {yearlySchedule.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-400 font-medium">
                Enter loan parameters above and click Calculate to generate your amortization
                schedule.
              </div>
            ) : (
              yearlySchedule.map((y) => {
                const isExpanded = !!expandedYears[y.yearNumber];
                return (
                  <div
                    key={y.yearNumber}
                    className="border border-gray-200 rounded-2xl overflow-hidden shadow-2xs"
                  >
                    <button
                      onClick={() => toggleYearExpand(y.yearNumber)}
                      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100/80 transition-colors text-left cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-100 text-brand-800 text-xs font-extrabold">
                          Y{y.yearNumber}
                        </span>
                        <div>
                          <p className="text-sm font-extrabold text-gray-900">
                            Year {y.yearNumber} Schedule
                          </p>
                          <p className="text-[11px] text-gray-500">
                            Opening: {formatShortCurrency(y.openingBalance)} → Closing:{' '}
                            {formatShortCurrency(y.closingBalance)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="hidden sm:flex items-center gap-4 text-xs">
                          <div>
                            <span className="text-[10px] text-gray-400 block uppercase">
                              Principal
                            </span>
                            <span className="font-bold text-brand-700">
                              {formatCurrency(y.principalPaid)}
                            </span>
                          </div>
                          <div>
                            <span className="text-[10px] text-gray-400 block uppercase">
                              Interest
                            </span>
                            <span className="font-bold text-amber-600">
                              {formatCurrency(y.interestPaid)}
                            </span>
                          </div>
                        </div>
                        <svg
                          className={`w-5 h-5 text-gray-400 transition-transform ${
                            isExpanded ? 'rotate-180' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                          />
                        </svg>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="p-2 sm:p-4 bg-white overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="border-b border-gray-150 text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                              <th className="py-2 px-2">Month</th>
                              <th className="py-2 px-2 text-right">Opening Bal</th>
                              <th className="py-2 px-2 text-right">EMI Paid</th>
                              <th className="py-2 px-2 text-right">Principal</th>
                              <th className="py-2 px-2 text-right">Interest</th>
                              <th className="py-2 px-2 text-right">Closing Bal</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 font-medium">
                            {y.months.map((m) => (
                              <tr key={m.monthNumber} className="hover:bg-gray-50/80">
                                <td className="py-2 px-2 font-bold text-gray-900">
                                  Mo {m.monthNumber}
                                </td>
                                <td className="py-2 px-2 text-right text-gray-500">
                                  {formatCurrency(m.openingBalance)}
                                </td>
                                <td className="py-2 px-2 text-right font-bold text-brand-800">
                                  {formatCurrency(m.emiPaid)}
                                </td>
                                <td className="py-2 px-2 text-right text-emerald-600 font-semibold">
                                  {formatCurrency(m.principalPaid)}
                                </td>
                                <td className="py-2 px-2 text-right text-amber-600 font-semibold">
                                  {formatCurrency(m.interestPaid)}
                                </td>
                                <td className="py-2 px-2 text-right text-gray-700 font-bold">
                                  {formatCurrency(m.closingBalance)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        ) : calculation.schedule.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-400 font-medium">
            Enter loan parameters above and click Calculate to generate your amortization schedule.
          </div>
        ) : (
          <table className="w-full text-left text-xs min-w-[600px]">
            <thead>
              <tr className="border-b border-gray-200 text-[11px] font-bold text-gray-400 uppercase tracking-wider bg-gray-50">
                <th className="py-3 px-3 rounded-l-xl">Month</th>
                <th className="py-3 px-3 text-right">Opening Balance</th>
                <th className="py-3 px-3 text-right">Monthly EMI</th>
                <th className="py-3 px-3 text-right">Principal Paid</th>
                <th className="py-3 px-3 text-right">Interest Paid</th>
                <th className="py-3 px-3 text-right rounded-r-xl">Closing Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 font-medium">
              {calculation.schedule.map((m) => (
                <tr key={m.monthNumber} className="hover:bg-gray-50/80 transition-colors">
                  <td className="py-2.5 px-3 font-bold text-gray-900">Month {m.monthNumber}</td>
                  <td className="py-2.5 px-3 text-right text-gray-500">
                    {formatCurrency(m.openingBalance)}
                  </td>
                  <td className="py-2.5 px-3 text-right font-bold text-brand-800">
                    {formatCurrency(m.emiPaid)}
                  </td>
                  <td className="py-2.5 px-3 text-right text-emerald-600 font-semibold">
                    {formatCurrency(m.principalPaid)}
                  </td>
                  <td className="py-2.5 px-3 text-right text-amber-600 font-semibold">
                    {formatCurrency(m.interestPaid)}
                  </td>
                  <td className="py-2.5 px-3 text-right text-gray-700 font-bold">
                    {formatCurrency(m.closingBalance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
